import React from "react";
import PropTypes from "prop-types";
import { StyledTableCell } from "../../../Alerts/AlertTables/materialUITableStyling";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";

const SortIcon = ({ active, order, color = "#fff", size = 18 }) => {
  if (!active) {
    return <div style={{ display: 'flex', flexDirection: 'column', opacity: 0.5 }}>
      <ArrowDropUpIcon sx={{ fontSize: 18, color, marginBottom: '-8px' }} />
      <ArrowDropDownIcon sx={{ fontSize: 18, color }} />
    </div>;
  }
  return order === "ASC"
    ? <ArrowDropDownIcon sx={{ fontSize: size, color, opacity: 1 }} />
    : <ArrowDropUpIcon sx={{ fontSize: size, color, opacity: 1 }} />;
};

SortIcon.propTypes = {
  active: PropTypes.bool,
  order: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.number,
};

export default function HeaderRow({
  styles,
  fixedColumns,
  fixedPaneWidth,
  FIXED_HEADER_HEIGHT,
  scrollableGroups,
  rightPaneWidth,
  GROUP_BAR_HEIGHT,
  SUBHEADER_HEIGHT,
  rightHeaderRef,
  // sorting props
  sortBy,
  sortOrder,      // "ASC" | "DESC"
  fieldsection,   // "" for left; group label for right children
  onLeftSort,
  onRightChildSort,
  onToggleFixedWidth,
  expandedFixed
}) {
  const isExpandableFixed = (label) =>
    label === "Basepack Desc" || label === "Action Recommended" || label === "Format" || label==="IC Action";

  return (
    <>
      {/* LEFT fixed header */}
      <div
        className={styles.leftFixHeader}
        style={{
          width: `${fixedPaneWidth}px`,
          minWidth: `${fixedPaneWidth}px`,
          maxWidth: `${fixedPaneWidth}px`,
        }}
      >
        {fixedColumns.map((col) => (
          <StyledTableCell
            key={`fixed-col-${col.label}`}
            component="div"
            role="columnheader"
            sx={{
              height: `${FIXED_HEADER_HEIGHT}px`,
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              whiteSpace: "nowrap",
              fontSize: 12,
              overflow: "hidden",
              textOverflow: "ellipsis",
              backgroundColor: "#354ACD",
              color: "#FFFFFF",
              flex: `0 0 ${col.width || 100}px`,
              minWidth: `${col.width || 100}px`,
              maxWidth: `${col.width || 100}px`,
              borderRight: "1px solid rgba(255,255,255,0.35)",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
            title={col.label}
            onClick={() => onLeftSort?.(col.label)}
          >
            {/* spread content so toggle sits far-right (away from sort icon) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              {/* Label + sort cluster (left side) */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{col.label}</span>
                <SortIcon
                  active={sortBy === col.label && (fieldsection || "") === ""}
                  order={sortOrder}
                  color="#FFFFFF"
                  size={16}
                />
              </div>

              {/* Far-right width toggle for heavy-content columns */}
              {isExpandableFixed(col.label) && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFixedWidth?.(col.label); }}
                  title="Expand/Collapse column"
                  aria-label={`Expand or collapse ${col.label} column`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    opacity: expandedFixed?.[col.label] ? 1 : 0.9,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  <UnfoldMoreRoundedIcon
                    sx={{
                      fontSize: 16,
                      color: "#FFFFFF",
                      transform: "rotate(90deg)",
                    }}
                  />
                </button>
              )}
            </div>
          </StyledTableCell>
        ))}
      </div>

      {/* RIGHT grouped header */}
      <div
        ref={rightHeaderRef}
        className={styles.hideTopScrollbar}
        style={{
          position: "relative",
          overflowX: "hidden",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          width: rightPaneWidth,
          minWidth: rightPaneWidth,
          background: "#FFFFFF",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex" }}>
          {scrollableGroups.map((grp, gIndex) => {
            const groupWidth =
              (grp.children?.length || 0) * (grp.widthPerCol || 100);
            const isEven = gIndex % 2 === 0;
            const iconColor = isEven ? "#111827" : "#FFFFFF";

            return (
              <StyledTableCell
                key={`group-${grp.group}`}
                component="div"
                role="columnheader"
                sx={{
                  flex: `0 0 ${groupWidth}px`,
                  minWidth: `${groupWidth}px`,
                  maxWidth: `${groupWidth}px`,
                  padding: 0,
                  backgroundColor: "transparent",
                  zIndex: 1,
                  borderRight: "1px solid #E6EAF2",
                  boxSizing: "border-box",
                }}
              >
                <div
                  className={styles.scrollables}
                  style={{
                    height: GROUP_BAR_HEIGHT,
                    borderBottom: `1px solid ${isEven ? "#4D5F6E" : "#D9E7E1"}`,
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: isEven ? "#C2E0FF" : "#5D6ED7",
                    }}
                  />
                  <div
                    className={styles.centeredLabelOverlay}
                    style={{
                      color: isEven ? "#1E2A44" : "#FFFFFF",
                      textShadow: isEven ? "none" : "0 1px 0 rgba(0,0,0,0.35)",
                    }}
                    title={grp.group}
                  >
                    {grp.group}
                  </div>
                </div>

                <div
                  className={styles.subHeaderRowContainer}
                  style={{
                    minHeight: SUBHEADER_HEIGHT,
                    height: "auto",
                    backgroundColor: isEven ? "#C2E0FF" : "#5D6ED7",
                  }}
                >
                  {grp.children.map((childLabel, cIndex) => {
                    const active =
                      sortBy === childLabel &&
                      (fieldsection || "") === (grp.group || "");
                    
                    let borderRightStyle;
                    if (cIndex === grp.children.length - 1) {
                      borderRightStyle = "none";
                    } else {
                      borderRightStyle = isEven
                        ? "1px solid #4D5F6E"
                        : "1px solid rgb(217, 231, 225)";
                    }
                    
                    return (
                      <button
                        key={`group-${grp.group}-child-${childLabel}`}
                        type="button"
                        className={styles.subHeaderRowChild}
                        style={{
                          flex: `0 0 ${grp.widthPerCol}px`,
                          minWidth: `${grp.widthPerCol}px`,
                          maxWidth: `${grp.widthPerCol}px`,
                          color: isEven ? "#0B1F44" : "#FFFFFF",
                          backgroundColor: isEven ? "#C2E0FF" : "#5D6ED7",
                          borderRight: borderRightStyle,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                          padding: "0 6px",
                          lineHeight: 1.15,
                          border: "none",
                          margin: 0,
                        }}
                        title={childLabel}
                        onClick={() => onRightChildSort?.(grp.group, childLabel)}
                      >
                        <div className={styles.subHeaderCell}>
                          <span className={styles.subHeaderCellLabel}>{childLabel}</span>
                          <SortIcon
                            active={active}
                            order={sortOrder}
                            color={iconColor}
                            size={16}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </StyledTableCell>
            );
          })}
        </div>
      </div>
    </>
  );
}

HeaderRow.propTypes = {
  styles: PropTypes.object,
  fixedColumns: PropTypes.array,
  fixedPaneWidth: PropTypes.number,
  FIXED_HEADER_HEIGHT: PropTypes.number,
  scrollableGroups: PropTypes.array,
  rightPaneWidth: PropTypes.string,
  GROUP_BAR_HEIGHT: PropTypes.number,
  SUBHEADER_HEIGHT: PropTypes.number,
  rightHeaderRef: PropTypes.object,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  fieldsection: PropTypes.string,
  onLeftSort: PropTypes.func,
  onRightChildSort: PropTypes.func,
  onToggleFixedWidth: PropTypes.func,
  expandedFixed: PropTypes.object,
};
